import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Button from 'components/inputs/button';
import { isDuctedBundleReady } from 'utils/ducted-bundle-picks';
import type { DuctedBundleSlot } from 'utils/ducted-bundle-picks-types';

type DuctedBundlePickFooterProps = Readonly<{
  slots: DuctedBundleSlot[];
  onClearPick: (systemUniqueId: string) => void;
  onAdd: (slots: DuctedBundleSlot[]) => void;
}>;

export default function DuctedBundlePickFooter({
  slots,
  onClearPick,
  onAdd
}: DuctedBundlePickFooterProps) {
  const ready = isDuctedBundleReady(slots);

  return (
    <div
      data-testid='ducted-bundle-pick-footer'
      className='flex shrink-0 flex-col gap-2 border-t border-gray-200 bg-white px-3 py-2'
    >
      <ul className='flex flex-col gap-1'>
        {slots.map((slot) => (
          <li
            key={slot.systemUniqueId}
            className='flex min-w-0 items-center gap-2 text-xs'
          >
            {slot.pick ? (
              <CheckCircleIcon className='h-4 w-4 shrink-0 text-green-600' />
            ) : (
              <span className='h-4 w-4 shrink-0 rounded-full border-2 border-dashed border-gray-300' />
            )}
            <span className='shrink-0 font-semibold uppercase tracking-wider text-gray-500'>
              {slot.pairName}
            </span>
            <span
              className={clsx(
                'min-w-0 grow truncate',
                slot.pick ? 'text-gray-900' : 'text-gray-500'
              )}
            >
              {slot.pick ? slot.pick.selection.title : 'Choose a system'}
            </span>
            {slot.pick && (
              <button
                type='button'
                aria-label={`Clear selection for ${slot.pairName}`}
                className='shrink-0 text-gray-400 hover:text-gray-600'
                onClick={() => onClearPick(slot.systemUniqueId)}
              >
                <XMarkIcon className='h-4 w-4' />
              </button>
            )}
          </li>
        ))}
      </ul>
      <Button
        color='primary'
        text='Add to Column'
        fullWidth
        disabled={!ready}
        tooltip={
          ready ? undefined : 'Select a system for every outdoor unit first'
        }
        onClick={() => onAdd(slots)}
      />
    </div>
  );
}
